// Making an account with a name from the grid's list, as Second Life's
// sign-up had it: a first name typed, a last name chosen, the two checked
// together as they are typed, then what the website asks as well. A grid
// with a website makes accounts there instead (login.ts); this is for one
// without. The grid checks everything again when the account is made.

import type { NewAccount } from "@/net/grid";
import {
  birthDateProblem,
  checkName,
  emailProblem,
  firstNameProblem,
  loginHandle,
  tidyFirstName,
  ADULT_AGE,
  type LastNames,
  type NameCheck,
} from "@/net/names";
import { el } from "./dom";
import { countries, guessCountry } from "./countries";
import "./signup.css";

/** How long typing pauses before the name is checked (ms). */
const CHECK_AFTER_MS = 350;

/** Said under the name until there is something to say about it. */
const NAME_HINT = "Type a first name and choose a last name. Your name is yours for good; a display name can be chosen later.";

/** The latest birth date that is old enough today, as YYYY-MM-DD. */
function latestBirthDate(today = new Date()): string {
  const y = today.getFullYear() - ADULT_AGE;
  const m = String(today.getMonth() + 1).padStart(2, "0");
  const d = String(today.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export class SignUpForm {
  readonly root: HTMLElement;
  private first: HTMLInputElement;
  private last: HTMLSelectElement;
  private line: HTMLElement;
  private email: HTMLInputElement;
  private birth: HTMLInputElement;
  private country: HTMLSelectElement;
  private invite: HTMLInputElement;
  private inviteField: HTMLElement;
  private inviteOnly = false;
  private firstMin = 2;
  private firstMax = 16;
  private timer = 0;
  private asking: AbortController | null = null;
  /** What the grid said of each name asked about, by the name in lower case. */
  private answers = new Map<string, NameCheck>();

  constructor(private gridUrl: () => string) {
    this.first = el("input", {
      type: "text",
      placeholder: "First name",
      autocomplete: "given-name",
      autocapitalize: "words",
      spellcheck: "false",
      maxlength: 16,
      "aria-label": "First name",
    });
    this.first.addEventListener("input", () => this.changed());
    // Shown as it will be kept once they have finished typing it.
    this.first.addEventListener("change", () => {
      this.first.value = tidyFirstName(this.first.value);
      this.changed();
    });
    this.last = el("select", { "aria-label": "Last name" });
    this.last.addEventListener("change", () => this.changed());
    this.line = el("div", { class: "name-line", "aria-live": "polite" }, NAME_HINT);

    this.email = el("input", { type: "email", placeholder: "you@example.com", autocomplete: "email", spellcheck: "false" });
    this.birth = el("input", { type: "date", min: "1900-01-01", max: latestBirthDate(), autocomplete: "bday", "aria-label": "Date of birth" });
    this.country = el("select", { autocomplete: "country", "aria-label": "Country you live in" },
      el("option", { value: "" }, "Choose…"),
      ...countries().map(([code, name]) => el("option", { value: code }, name)),
    );
    this.country.value = guessCountry();
    this.invite = el("input", { type: "text", placeholder: "The code you were given", autocomplete: "off", spellcheck: "false" });
    this.inviteField = el("div", { class: "field" }, el("label", {}, "Invite code"), this.invite);

    this.root = el("div", { class: "sign-up" },
      el("div", { class: "field" },
        el("label", {}, "Your name"),
        el("div", { class: "pair" }, this.first, this.last),
        this.line,
      ),
      el("div", { class: "field" }, el("label", {}, "Email"), this.email),
      el("div", { class: "pair" },
        el("div", { class: "field" }, el("label", {}, "Date of birth"), this.birth),
        el("div", { class: "field" }, el("label", {}, "Country"), this.country),
      ),
      this.inviteField,
    );
  }

  /** Ready the form with the last names on offer now, and the invite code if the grid asks one. */
  show(pool: LastNames, inviteOnly: boolean): void {
    this.firstMin = pool.firstMin;
    this.firstMax = pool.firstMax;
    this.first.maxLength = pool.firstMax;
    const chosen = this.last.value;
    this.last.replaceChildren(
      el("option", { value: "" }, pool.names.length > 0 ? "Last name…" : "None offered"),
      ...pool.names.map((n) => el("option", { value: n }, n)),
    );
    this.last.value = pool.names.includes(chosen) ? chosen : "";
    this.inviteOnly = inviteOnly;
    this.inviteField.style.display = inviteOnly ? "" : "none";
    // Names taken since the form was last open are asked about again.
    this.answers.clear();
    this.changed();
  }

  focus(): void {
    this.first.focus();
  }

  /** 'Nova Starwick', as the account will be called. */
  accountName(): string {
    return `${tidyFirstName(this.first.value)} ${this.last.value}`;
  }

  /** What is still wrong, in plain words, or null when it can be sent. */
  problem(): string | null {
    const first = firstNameProblem(this.first.value, this.firstMin, this.firstMax);
    if (first) return first;
    if (!this.last.value) {
      return this.last.options.length > 1 ? "Choose a last name from the list." : "No last names are offered just now. Ask the grid's staff.";
    }
    const known = this.answers.get(this.accountName().toLowerCase());
    if (known && !known.available) return known.message || "That name is not available.";
    return emailProblem(this.email.value)
      ?? birthDateProblem(this.birth.value)
      ?? (this.country.value ? null : "Choose the country you live in.")
      ?? (this.inviteOnly && !this.invite.value.trim() ? "Type the invite code you were given." : null);
  }

  /** The account to ask for, all but its password and the terms agreed to. */
  account(): Omit<NewAccount, "password" | "acceptTerms"> {
    const first = tidyFirstName(this.first.value);
    const last = this.last.value;
    return {
      username: loginHandle(first, last),
      displayName: "",
      email: this.email.value.trim(),
      inviteCode: this.inviteOnly ? this.invite.value.trim() : "",
      firstName: first,
      lastName: last,
      birthDate: this.birth.value,
      country: this.country.value,
    };
  }

  /** The grid said the name went to somebody else in the meantime: asked about again next time. */
  forget(): void {
    this.answers.delete(this.accountName().toLowerCase());
    this.changed();
  }

  private say(text: string, tone: "" | "ok" | "bad" = "", ...after: Node[]): void {
    this.line.className = `name-line ${tone}`.trim();
    this.line.replaceChildren(text, ...after);
  }

  /** Something was typed or chosen: say what is known, and ask the grid once typing pauses. */
  private changed(): void {
    window.clearTimeout(this.timer);
    this.asking?.abort();
    this.asking = null;
    const typed = this.first.value.trim();
    if (!typed) return this.say(NAME_HINT);
    const problem = firstNameProblem(typed, this.firstMin, this.firstMax);
    if (problem) return this.say(problem, "bad");
    const first = tidyFirstName(typed);
    const last = this.last.value;
    if (!last) return this.say(`Now choose a last name to go with ${first}.`);
    const known = this.answers.get(`${first} ${last}`.toLowerCase());
    if (known) return this.answered(first, known);
    this.say("Checking…");
    this.timer = window.setTimeout(() => void this.ask(first, last), CHECK_AFTER_MS);
  }

  private async ask(first: string, last: string): Promise<void> {
    const asking = new AbortController();
    this.asking = asking;
    try {
      const answer = await checkName(this.gridUrl(), first, last, asking.signal);
      if (asking.signal.aborted) return;
      if (typeof answer === "string") return this.say(answer, "bad");
      this.answers.set(`${first} ${last}`.toLowerCase(), answer);
      this.answered(first, answer);
    } catch {
      if (asking.signal.aborted) return;
      // Not checked now, but checked anyway when the account is made.
      this.say("The name could not be checked just now. It is checked again when you make the account.");
    }
  }

  private answered(first: string, answer: NameCheck): void {
    if (answer.available) {
      const name = answer.name || `${first} ${this.last.value}`;
      const login = answer.login || loginHandle(first, this.last.value);
      return this.say(`${name} is available. It is yours for good: you sign in as ${name}, or ${login}.`, "ok");
    }
    // Only those still in the list, which may have changed since it came.
    const offered = new Set(Array.from(this.last.options, (o) => o.value));
    const others = answer.others.filter((n) => n !== this.last.value && offered.has(n));
    if (others.length === 0) return this.say(answer.message || "That name is not available.", "bad");
    const choices = others.map((n) =>
      el("button", { type: "button", class: "link", onClick: () => this.choose(n) }, n),
    );
    this.say(`${answer.message || "That name is not available."} Free with ${first}: `, "bad", ...choices);
  }

  private choose(last: string): void {
    this.last.value = last;
    this.changed();
  }
}
